import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Select,
  Tabs,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from "components/ui";
import {
  CONTENT_LANGUAGES,
  MAX_CONTENT_LANGUAGES,
  contentLanguageLabel,
} from "constants/contentLanguages";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdContentCopy, MdDelete } from "react-icons/md";

const DEFAULT_TAB = "default";

export type TranslationFieldType = "input" | "textarea";

export interface TranslationFieldSpec {
  /** Matches the key used in both `defaultValues` and each translation entry. */
  key: string;
  /** Already-translated field label. */
  label: string;
  type: TranslationFieldType;
  /** Required on the Default tab only — translations may always be partial. */
  required?: boolean;
  maxLength?: number;
  /** Textarea only. */
  rows?: number;
  /** Default tab only; language tabs show the default value as their placeholder. */
  placeholder?: string;
  helperText?: string;
}

/**
 * Field key → value for one language. Values are optional because a translation may
 * cover only some fields; the missing ones fall back to the default text.
 */
export type TranslationValues = Record<string, string | undefined>;

export interface TranslationTabsProps {
  fields: TranslationFieldSpec[];
  /** Base content — what visitors see when their language has no translation. */
  defaultValues: TranslationValues;
  onDefaultChange: (next: TranslationValues) => void;
  /** Language code → partial field values. */
  translations: Record<string, TranslationValues>;
  onTranslationsChange: (next: Record<string, TranslationValues>) => void;
  /** Validation errors for the Default tab, keyed by field key. */
  errors?: Record<string, string>;
  maxLanguages?: number;
  disabled?: boolean;
}

/**
 * Editor for content that can be authored once per language: a "Default" tab holding
 * the base text plus one tab per added language.
 *
 * Shared by the shortcut-card modal, the prompt-shortcut modal, and the welcome-screen
 * text field, so all three behave identically. A field left blank inside a language tab
 * falls back to the default text at render time — partial translations are intentional,
 * not an error state.
 *
 * Fully controlled: the only internal state is which tab is open, which is what keeps it
 * safe under StrictMode's double render (no prop→state effects to run twice).
 */
export function TranslationTabs({
  fields,
  defaultValues,
  onDefaultChange,
  translations,
  onTranslationsChange,
  errors,
  maxLanguages = MAX_CONTENT_LANGUAGES,
  disabled,
}: TranslationTabsProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>(DEFAULT_TAB);

  const languageCodes = useMemo(
    () => Object.keys(translations),
    [translations],
  );

  const availableLanguages = useMemo(
    () =>
      CONTENT_LANGUAGES.filter((l) => !languageCodes.includes(l.code)).map(
        (l) => ({ label: l.nativeName, value: l.code }),
      ),
    [languageCodes],
  );

  const atLanguageLimit = languageCodes.length >= maxLanguages;

  const setDefaultField = (key: string, value: string) =>
    onDefaultChange({ ...defaultValues, [key]: value });

  const setTranslationField = (code: string, key: string, value: string) =>
    onTranslationsChange({
      ...translations,
      [code]: { ...translations[code], [key]: value },
    });

  const addLanguage = (code: string) => {
    if (!code || translations[code] || atLanguageLimit) return;
    onTranslationsChange({ ...translations, [code]: {} });
    setActiveTab(code);
  };

  const removeLanguage = (code: string) => {
    const next = { ...translations };
    delete next[code];
    onTranslationsChange(next);
    setActiveTab(DEFAULT_TAB);
  };

  const copyFromDefault = (code: string) => {
    const copied: TranslationValues = {};
    fields.forEach((f) => {
      copied[f.key] = defaultValues[f.key] ?? "";
    });
    onTranslationsChange({ ...translations, [code]: copied });
  };

  /** A language entry is incomplete while any required field is still blank. */
  const isIncomplete = (code: string) =>
    fields.some((f) => f.required && !translations[code]?.[f.key]?.trim());

  const renderField = (
    field: TranslationFieldSpec,
    value: string,
    onChange: (next: string) => void,
    isDefaultTab: boolean,
  ) => {
    const shared = {
      label: field.label,
      value,
      onChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      ) => onChange(e.target.value),
      maxLength: field.maxLength,
      disabled,
      // On a language tab the base text becomes the placeholder, so the admin can
      // see what they are translating without leaving the field.
      placeholder: isDefaultTab
        ? field.placeholder
        : defaultValues[field.key] || field.placeholder,
      required: isDefaultTab ? field.required : undefined,
      errorText: isDefaultTab ? errors?.[field.key] : undefined,
      helperText: isDefaultTab ? field.helperText : undefined,
    };

    return field.type === "textarea" ? (
      <Textarea
        key={field.key}
        rows={field.rows ?? 3}
        resize="vertical"
        {...shared}
      />
    ) : (
      <Input key={field.key} {...shared} />
    );
  };

  return (
    <Tabs.Root
      value={activeTab}
      onValueChange={(e: { value: string }) => setActiveTab(e.value)}
      variant="line"
      size="sm"
    >
      <HStack align="center" gap={3} mb={1}>
        <Tabs.List overflowX="auto" flex={1} minW={0}>
          <Tabs.Trigger value={DEFAULT_TAB}>
            {t("app.translations.defaultTab", { defaultValue: "Default" })}
          </Tabs.Trigger>
          {languageCodes.map((code) => (
            <Tabs.Trigger key={code} value={code}>
              <HStack gap={1.5}>
                <span>{contentLanguageLabel(code)}</span>
                {isIncomplete(code) && (
                  <Tooltip
                    content={t("app.translations.incompleteTooltip", {
                      defaultValue:
                        "Some fields are empty and will fall back to the default text.",
                    })}
                  >
                    <Badge size="sm" colorPalette="orange">
                      {t("app.translations.incompleteBadge", {
                        defaultValue: "Partial",
                      })}
                    </Badge>
                  </Tooltip>
                )}
              </HStack>
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        {/* Outside Tabs.List on purpose: Chakra's roving tabindex treats every list
            child as a trigger, and at many languages the control would scroll away. */}
        <Box flexShrink={0} minW="180px">
          <Tooltip
            content={t("app.translations.maxLanguagesReached", {
              count: maxLanguages,
              defaultValue: `Maximum ${maxLanguages} languages per item.`,
            })}
            disabled={!atLanguageLimit}
          >
            <Box>
              <Select
                size="sm"
                value={[]}
                items={availableLanguages}
                placeholder={t("app.translations.addLanguage", {
                  defaultValue: "+ Add language",
                })}
                disabled={disabled || atLanguageLimit}
                onValueChange={(details: { value: string[] }) =>
                  addLanguage(details.value[0] ?? "")
                }
              />
            </Box>
          </Tooltip>
        </Box>
      </HStack>

      <Tabs.Content value={DEFAULT_TAB}>
        <VStack align="stretch" gap={3} pt={2}>
          {fields.map((field) =>
            renderField(
              field,
              defaultValues[field.key] ?? "",
              (next) => setDefaultField(field.key, next),
              true,
            ),
          )}
          {languageCodes.length === 0 && (
            <Text fontSize="xs" color="var(--text-secondary-color)">
              {t("app.translations.emptyState", {
                defaultValue:
                  "No translations yet. Visitors see this default text whatever their language.",
              })}
            </Text>
          )}
        </VStack>
      </Tabs.Content>

      {languageCodes.map((code) => (
        <Tabs.Content key={code} value={code}>
          <VStack align="stretch" gap={3} pt={2}>
            <HStack justify="space-between" align="center">
              <Text fontSize="xs" color="var(--text-secondary-color)">
                {t("app.translations.fallbackHint", {
                  defaultValue:
                    "Leave a field empty to fall back to the default text.",
                })}
              </Text>
              <HStack gap={1}>
                <Button
                  intent="ghost"
                  size="xs"
                  disabled={disabled}
                  onClick={() => copyFromDefault(code)}
                >
                  <MdContentCopy />
                  {t("app.translations.copyFromDefault", {
                    defaultValue: "Copy from default",
                  })}
                </Button>
                <Tooltip
                  content={t("app.translations.removeLanguage", {
                    defaultValue: "Remove this translation",
                  })}
                >
                  <IconButton
                    aria-label={t("app.translations.removeLanguage", {
                      defaultValue: "Remove this translation",
                    })}
                    variant="ghost"
                    size="xs"
                    colorPalette="red"
                    disabled={disabled}
                    onClick={() => removeLanguage(code)}
                  >
                    <MdDelete />
                  </IconButton>
                </Tooltip>
              </HStack>
            </HStack>
            {fields.map((field) =>
              renderField(
                field,
                translations[code]?.[field.key] ?? "",
                (next) => setTranslationField(code, field.key, next),
                false,
              ),
            )}
          </VStack>
        </Tabs.Content>
      ))}
    </Tabs.Root>
  );
}

/**
 * Drop language entries whose every field is blank. Call before sending a save
 * payload so the API never stores an entry the resolver would fall straight through.
 * (The backend enforces the same rule; this just keeps the request clean.)
 */
export function pruneEmptyTranslations<T extends TranslationValues>(
  translations: Record<string, T>,
): Record<string, T> {
  const result: Record<string, T> = {};
  Object.entries(translations).forEach(([code, values]) => {
    const hasValue = Object.values(values ?? {}).some(
      (v) => typeof v === "string" && v.trim().length > 0,
    );
    if (hasValue) result[code] = values;
  });
  return result;
}

export default TranslationTabs;
